import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';

import React, { useEffect, useState } from 'react';
import { Flex, Box, Text, Button } from 'rebass';
import { Switch } from '@rebass/forms';
import { IoIosArrowRoundBack } from 'react-icons/io';
import store from 'store';
import { DateTime } from 'luxon';
import useSWR from 'swr';
import { useCreateNewAccount } from 'auth/hooks';
import { useLocation } from 'react-router-dom';
import { useMobileCheckHook } from 'story-media/hooks';
import { useCurrentMember } from 'members/hooks/index';
import { SelectNonFormikField } from 'app/form/formik/select/index';
import useEntity from 'entities/hooks/use-entity';
import { findStoryId } from 'app/navigation/sidebar/index';
import { patchChildJournalReminder, publicPatchChildJournalReminder } from 'child-journals/api';
import { getResourcePath } from 'collections/api';
import { useTimezoneSelect, allTimezones } from "react-timezone-select"
import Banner from 'app/payment/Banner';
import { useMyChildJournals } from 'account-dashboard/hooks';

function getNextReminder(dateString, values, timeZoneOptions) {
  const { time, 'time-zone': timeZone } = values;

  const [hours, minutes, seconds] = time.split(':').map(Number);

  const now = DateTime.utc().set({ hour: hours, minute: minutes, second: seconds, millisecond: 0 });

  const selectedTimeZone = timeZoneOptions.find(({ value }) => value === timeZone)
  let nextReminder = now;

  const formattedTime = nextReminder.toFormat("h:mm a"); // Time with AM/PM
  const zoneAbbreviation = nextReminder.offsetNameShort;  // Get time zone abbreviation (e.g., EST, PDT)

  const [datePart, timePart] = dateString.split('T'); // Split date and time
  const [year, month, day] = datePart.split('-');  // Get year, month, day from the date part

  const date = new Date(`${year}-${month}-${day}T${time}`); // This doesn't apply any timezone offset
  date.setHours(hours, minutes, seconds, 0); // Set the time portion without considering timezones

  const options = { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' };
  let formattedDate = date.toLocaleDateString('en-US', options);

  const dayS = date.getDate();
  const suffix = dayS === 1 || dayS === 21 || dayS === 31 ? 'st' :
    dayS === 2 || dayS === 22 ? 'nd' :
      dayS === 3 || dayS === 23 ? 'rd' : 'th';
  formattedDate = formattedDate.replace(/\d+/, dayS + suffix);

  return `Your next reminder will be sent at ${formattedTime} on ${formattedDate}${selectedTimeZone ? ` (in ${selectedTimeZone?.label.replace(/[()]/g, '')})` : ''}.`;
}

const labelStyle = "original"
const timezones = {
  ...allTimezones,
  "Europe/Berlin": "Frankfurt",
}

const howOftenOptions = [
  { label: 'Weekly', value: 'weekly' },
  { label: '2 weeks', value: '2 weeks' },
  { label: '3 weeks', value: '3 weeks' },
  { label: '4 weeks', value: '4 weeks' },
]

const weedDayOptions = [
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
  { label: 'Sunday', value: 'sunday' },
]

function generateHours() {
  const hours = [];
  for (let i = 0; i < 24; i++) {
    const label = `${i < 10 ? `0${i}` : i}:00`;  // 'hh:00' format for label
    const value = `${i < 10 ? `0${i}` : i}:00:00`;  // 'hh:00:00' format for value
    hours.push({ label, value });
  }
  return hours;
}

function getIntervalUnit(intervalUnit, interval) {
  if (intervalUnit === 'week' && interval === 1) {
    return 'weekly'
  } else if (intervalUnit === 'week' && interval === 2) {
    return '2 weeks'
  }
  else if (intervalUnit === 'week' && interval === 3) {
    return '3 weeks'
  }
  else if (intervalUnit === 'week' && interval === 4) {
    return '4 weeks'
  }
}

const timeOptions = generateHours();

export const label = (text, isRequired) => <Text
  sx={{
    color: "#64748B",
    fontFeatureSettings: 'liga off, calt off',
    fontFamily: 'Poppins',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '20px', /* 142.857% */
    letterSpacing: '-0.096px',
  }}> {text} {isRequired && <span style={{ color: 'red' }}>*</span >}</Text >

const NotificationSettings = ({ firePostMemberEvent, secret, child_journal_id, onStepDown }) => {
  const { options } = useTimezoneSelect({ labelStyle, timezones })
  const [success, setSuccess] = useState(false)
  const [updatedRunAt, setUpdatedRunAt] = useState(null)
  const { goBack } = useHistory();
  const { journals } = useMyChildJournals();
  const hasAssignedJournal = journals.every((journal) => journal?.state !== "initialized");

  const { pathname } = useLocation();
  const locationParams = pathname.split('/');
  const isFromMobile = useMobileCheckHook()
  const journalId = child_journal_id || findStoryId(locationParams)

  const member = useCurrentMember()
  const createNewUser = useCreateNewAccount();
  const path = getResourcePath({
    ...{ id: journalId, type: 'child-journals' }
  });
  const { entity: childJournal } = useEntity({ type: 'child-journals', id: journalId })

  const { entity: reminders } = useEntity(childJournal?.reminder)
  const { revalidate: revalidateJournal } = useSWR(pathname.includes('/snooze') ? `/v3/public/child_journals/${reminders?.['remindable-id']}` : `${path}`);

  async function createNewUserRequest() {
    if (!store.get('auth_token'))
      await createNewUser();
    if (firePostMemberEvent) {
      if (isFromMobile && pathname.includes('/get-started')) {
        firePostMemberEvent('testimonials')
      } else {
        firePostMemberEvent('create_account')
      }
    }
  }

  useEffect(() => {
    if (!member?.['has-username-password-identifier'])
      createNewUserRequest()
  }, [member?.['has-username-password-identifier']])

  function setInitialValues() {
    return {
      enabled: reminders?.enabled,
      'interval': reminders?.schedule?.['interval'],
      'interval-unit': reminders?.schedule?.['interval-unit'] || '',
      'weekday': reminders?.schedule?.['weekday'] || '',
      time: reminders?.schedule?.['time'] || '',
      "time-zone": reminders?.schedule?.['time-zone'] || '',
    }
  }

  const onSubmit = async (values, { setSubmitting }) => {
    const { enabled, ...schedule } = setInitialValues()
    const finalSchedule = { interval: 1, ...schedule, ...values }

    setSuccess(false)
    const data = {
      id: childJournal?.reminder?.id,
      type: "reminders",
      attributes: {
        schedule: finalSchedule,
        enabled: values.enabled, // Ensure enabled is set, default to true if not provided
      }
    }
    let resp;
    setSubmitting(true)
    if (secret)
      resp = await publicPatchChildJournalReminder(secret, data)
    else
      resp = await patchChildJournalReminder(childJournal?.reminder?.id, data)

    setUpdatedRunAt(resp?.data?.initial?.data?.attributes?.['run-at'])
    revalidateJournal()
    setSubmitting(false)
    setSuccess(true)
  }

  function setShowValidationSchema() {
    return Yup.object().shape({
      enabled: Yup.boolean(),
      'interval-unit': Yup.string(),
      'weekday': Yup.string(),
      time: Yup.string(),
      'time-zone': Yup.string(),
    })
  }

  return (
    <Formik
      initialValues={setInitialValues()}
      enableReinitialize
      validationSchema={setShowValidationSchema()}
      onSubmit={onSubmit}
    >
      {({ values, isSubmitting, handleSubmit, setFieldValue }) => (
        <Form>
          <Flex flexDirection="column" m="auto" mx={[2, "40px"]}>
            {secret ?
              <Text
                mt="30px"
                color="codGrey"
                fontFamily="Poppins"
                fontSize={22}
                lineHeight="165%"
                letterSpacing="0.22"
                mb={1}
              >
                Reminders Settings
              </Text>
              : <Box>
                <Text
                  mt="50px"
                  color="codGrey"
                  fontFamily="Poppins"
                  fontSize={22}
                  lineHeight="165%"
                  letterSpacing="0.22"
                  mb={1}
                >
                  Set Reminders
                </Text>
                <Box sx={{ border: "0.5px solid #E2E8F0" }} my={4} />
              </Box>}
            <Box width="100%" maxWidth={secret ? 'unset' : "500px"} >
              {success && reminders?.['run-at'] && <Banner
                bg='successBg'
                color='success'
                heading={reminders?.enabled ? getNextReminder(updatedRunAt || reminders?.['run-at'], values, options) : "Reminders have been disabled."}
                p="16px"
                size={'small'}
              />}
              <Flex my={3} alignItems="center">
                <Text
                  mr={4}
                  color="#000"
                  fontFamily="Poppins"
                  fontSize={18}
                  lineHeight="165%"
                  letterSpacing="0.22"
                >Reminders</Text>
                <Switch
                  className="switch normalize"
                  checked={values?.enabled}
                  sx={{
                    width: 45,
                    height: 30,
                    border: 'unset',
                    borderRadius: '20px',
                    background: values?.enabled ? '#9B59B6' : 'grey',
                    '&:focus': {
                      boxShadow: 'unset',
                    },
                  }}
                  onClick={() => {
                    setSuccess(false)
                    setFieldValue('enabled', !values?.enabled)
                    // onSubmit({ enabled: !values?.enabled })
                  }}
                />
              </Flex>

              <Box my={3}>
                <SelectNonFormikField
                  disabled={!values?.enabled}
                  label={label('How often?')}
                  options={howOftenOptions}
                  value={getIntervalUnit(values?.['interval-unit'], values?.['interval'])}
                  onChange={(option) => {

                    let interval = 0
                    let intervalUnit = ''
                    if (option.value === 'weekly') {
                      interval = 1;
                      intervalUnit = 'week';
                    } else if (option.value === '2 weeks') {
                      interval = 2;
                      intervalUnit = 'week';
                    }
                    else if (option.value === '3 weeks') {
                      interval = 3;
                      intervalUnit = 'week';
                    }
                    else if (option.value === '4 weeks') {
                      interval = 4;
                      intervalUnit = 'week';
                    }
                    setSuccess(false)
                    setFieldValue('interval', interval)
                    setFieldValue('interval-unit', intervalUnit)

                    // onSubmit({ schedule: { interval, 'interval-unit': intervalUnit } })

                  }}
                  name="interval-unit"
                  width="100%"
                />
              </Box>

              <Box my={3}>
                <SelectNonFormikField
                  disabled={!values?.enabled}
                  label={label('Which day?')}
                  options={weedDayOptions}
                  value={values?.['weekday']}
                  onChange={(option) => {
                    setSuccess(false)
                    setFieldValue('weekday', option.value)
                    // onSubmit({ schedule: { 'weekday': option.value } })
                  }}
                  name="weekday"
                  width="100%"
                />
              </Box>

              <Box my={3}>
                <SelectNonFormikField
                  disabled={!values?.enabled}
                  label={label('Which time?')}
                  options={timeOptions}
                  value={values?.['time']}
                  onChange={(option) => {
                    setSuccess(false)
                    setFieldValue('time', option.value)
                    // onSubmit({ schedule: { 'time': option.value } })
                  }}
                  name="time"
                  width="100%"
                />
              </Box>

              <Box my={3}>
                <SelectNonFormikField
                  disabled={!values?.enabled}
                  label={label('Your timezone?')}
                  options={options}
                  value={values?.['time-zone']}
                  onChange={(option) => {
                    setSuccess(false)
                    setFieldValue('time-zone', option.value)
                    // onSubmit({ schedule: { 'time-zone': option.value } })
                  }}
                  name="time-zone"
                  width="100%"
                />
              </Box>
            </Box>
            <Text my={1}
              sx={{
                color: '#000',
                fontFamily: 'Poppins',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: '400',
                lineHeight: '165%', /* 26.4px */
                letterSpacing: '0.16px',
              }}>Save these changes?</Text>
            <Flex justifyContent={hasAssignedJournal ? 'flex-start' : "space-between"} alignItems="center" mt={2}>
              {(hasAssignedJournal || secret) && <Flex
                height={40}
                justifyContent="space-around"
                alignItems="center"
                bg="#fff"
                sx={{ borderRadius: 'medium', cursor: 'pointer' }}
                onClick={hasAssignedJournal ? goBack : onStepDown}
                mr={hasAssignedJournal ? 7 : 0}
              >
                <Text as={IoIosArrowRoundBack} color="mineShaft" minWidth="24px" fontSize={24} />
                <Text minWidth={'max-content'} color="mineShaft" fontFamily="Poppins" lineHeight={'150%'} ml={1} fontWeight={400} fontSize="15px">
                  Back
                </Text>
              </Flex>}
              <Flex>

              </Flex>
              <Button
                height={40}
                py={1}
                px={5}
                onClick={handleSubmit}
                // disabled={!values?.enabled}
                bg={isSubmitting ? "grey" : "#9B59B6"}
                type="submit"
                sx={{ border: 'transparent', fontWeight: 500, borderRadius: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              >
                Save
              </Button>
            </Flex>
          </Flex>
        </Form>
      )
      }
    </Formik >

  );
};

export default NotificationSettings;
